import { Box, GlobalStyles, Paper, ThemeProvider, Typography } from '@mui/material'
import { enUS, plPL } from '@mui/material/locale'
import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import EnvBadge from '@/components/EnvBadge'
import SygnetCluster from '@/components/researcher/SygnetCluster'
import { useLocale } from '@/contexts/LocaleContext'
import { createAffexyTheme } from '@/theme/affexyTheme'

type AuthShellProps = {
  /** Card heading. */
  title: ReactNode
  /** Supporting copy under the heading. */
  subtitle?: ReactNode
  /** Optional accent icon shown above the heading (e.g. a lock glyph). */
  icon?: ReactNode
  /** Tone of the accent icon badge. */
  iconTone?: 'primary' | 'success' | 'error'
  /** Form / body content. */
  children: ReactNode
}

const keyframes = (
  <GlobalStyles
    styles={{
      '@keyframes affexy-auth-float': {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-10px)' },
      },
    }}
  />
)

/**
 * Affexy auth chrome — a light "data-confident" canvas with a navy brand
 * panel, mirroring the researcher dashboard look. Scopes the Affexy MUI theme
 * via a nested ThemeProvider so the auth flow matches the in-app design without
 * touching the app-wide (currently dark) theme.
 */
export default function AuthShell({ title, subtitle, icon, iconTone = 'primary', children }: AuthShellProps) {
  const { language } = useLocale()
  const theme = useMemo(() => createAffexyTheme(language === 'pl' ? plPL : enUS), [language])
  const { t } = useTranslation('auth')
  const { t: tLayout } = useTranslation('layout')
  const c = theme.affexy.colors

  const toneColor =
    iconTone === 'success' ? c.success : iconTone === 'error' ? c.danger : c.coral
  const toneBg =
    iconTone === 'success'
      ? 'rgba(95,179,168,.14)'
      : iconTone === 'error'
        ? 'rgba(201,58,53,.12)'
        : c.coralSoft

  return (
    <ThemeProvider theme={theme}>
      {keyframes}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          bgcolor: 'background.default',
          color: 'text.primary',
          fontFamily: theme.affexy.fonts.body,
        }}
      >
        {/* ── Brand panel (desktop only) ─────────────────────────────── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'relative',
            overflow: 'hidden',
            width: { md: '44%', lg: '40%' },
            maxWidth: 560,
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: { md: 5, lg: 6 },
            background: theme.affexy.gradients.ctaCard,
            color: c.white,
          }}
        >
          {/* Decorative sygnet ornament */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              right: -120,
              top: -80,
              width: 460,
              height: 460,
              opacity: 0.16,
              animation: 'affexy-auth-float 9s ease-in-out infinite',
            }}
          >
            <SygnetCluster />
          </Box>

          {/* Brand lockup */}
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36 }}>
              <SygnetCluster />
            </Box>
            <Typography
              sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 22, letterSpacing: '-0.01em' }}
            >
              {t('appTitle')}
            </Typography>
          </Box>

          {/* Tagline */}
          <Box sx={{ position: 'relative', maxWidth: 380 }}>
            <Typography
              sx={{
                fontFamily: theme.affexy.fonts.heading,
                fontWeight: 700,
                fontSize: { md: 32, lg: 38 },
                lineHeight: 1.12,
                letterSpacing: '-0.02em',
                mb: 2,
              }}
            >
              {tLayout('brandSubtitle')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,.6)', fontSize: 15, lineHeight: 1.6 }}>
              {t('brandPitch', {
                defaultValue:
                  'Capture, measure and understand emotional response across your research studies.',
              })}
            </Typography>
          </Box>

          {/* Footer */}
          <Typography sx={{ position: 'relative', color: 'rgba(255,255,255,.45)', fontSize: 12 }}>
            © {t('appTitle')}
          </Typography>
        </Box>

        {/* ── Form panel ─────────────────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2.5, sm: 4 },
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <LanguageSwitcher />
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 440,
              borderRadius: `${theme.affexy.radii.lg}px`,
              boxShadow: theme.affexy.shadows.card,
              border: `1px solid ${c.ink08}`,
              p: { xs: 3, sm: 4.5 },
            }}
          >
            {/* Logo — shown here since the brand panel is hidden on mobile and
                provides a consistent mark on every breakpoint. */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 3 }}>
              <img src={`${import.meta.env.BASE_URL}affexy-logo.png`} alt={t('appTitle')} style={{ height: 40 }} />
              <EnvBadge />
            </Box>

            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 3 }}>
              {icon && (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    mb: 2,
                    borderRadius: `${theme.affexy.radii.md}px`,
                    bgcolor: toneBg,
                    color: toneColor,
                    display: 'grid',
                    placeItems: 'center',
                    '& svg': { width: 28, height: 28 },
                  }}
                >
                  {icon}
                </Box>
              )}
              <Typography
                component="h1"
                sx={{
                  fontFamily: theme.affexy.fonts.heading,
                  fontWeight: 700,
                  fontSize: 24,
                  letterSpacing: '-0.01em',
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ color: c.ink56, mt: 1, maxWidth: 320 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>

            {children}
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
